import { Route, Routes } from '@angular/router';
import { IsAuthenticatedGuard } from '@core/guards/is-authenticated.guard';
import { IsNotAuthenticatedGuard } from '@core/guards/is-not-authenticated.guard';
import { IsStaffGuard } from '@core/guards/is-staff.guard';
import { IsVerifiedGuard } from '@core/guards/is-verified.guard';
import {
    FeatureFlagResolver,
    FeatureFlags,
} from '@core/services/feature-flag.service';
import { startImpersonationGuard } from '@features/impersonation/guards/start-impersonation.guard';
import { provideImpersonation } from '@features/impersonation/provide-impersonation';

const verifyEmailRoute: Route = {
    path: 'verify-email',
    loadComponent: () =>
        import('./pages/user-pages/user-email-verify-page/user-email-verify-page.component').then(
            m => m.UserEmailVerifyPageComponent
        ),
};

const dashboardRoutes: Routes = [
    {
        path: '',
        redirectTo: 'trip-preparation',
        pathMatch: 'full',
    },
    {
        path: 'invoices',
        canActivate: [IsAuthenticatedGuard],
        loadComponent: () =>
            import('./pages/client-dashboard-page/client-dashboard-page.component').then(
                m => m.ClientDashboardPageComponent
            ),
    },
    {
        path: 'credits',
        canActivate: [IsAuthenticatedGuard],
        loadComponent: () =>
            import('./pages/client-dashboard-credits-page/client-dashboard-credits-page.component').then(
                m => m.ClientDashboardCreditsPageComponent
            ),
    },
    {
        path: 'my-profile',
        canActivate: [IsAuthenticatedGuard],
        loadComponent: () =>
            import('./pages/client-dashboard-profile-page/client-dashboard-profile-page.component').then(
                m => m.ClientDashboardProfilePageComponent
            ),
    },
    {
        path: 'trip-preparation',
        canActivate: [IsAuthenticatedGuard],
        loadComponent: () =>
            import('./pages/client-dashboard-trip-preparation-page/client-dashboard-trip-preparation-page.component').then(
                m => m.ClientDashboardTripPreparationPageComponent
            ),
    },
];

export const appRoutes: Routes = [
    {
        path: 'my-profile',
        redirectTo: '/sign-in',
        pathMatch: 'full',
    },
    {
        path: 'sign-in',
        canActivate: [IsNotAuthenticatedGuard],
        loadComponent: () =>
            import('./pages/user-pages/user-login-page/user-login-page.component').then(
                m => m.UserLoginPageComponent
            ),
    },
    {
        path: 'logout',
        canActivate: [IsAuthenticatedGuard],
        loadComponent: () =>
            import('./pages/user-pages/user-logout-page/user-logout-page.component').then(
                m => m.UserLogoutPageComponent
            ),
    },
    verifyEmailRoute,

    {
        path: 'not-authenticated',
        loadComponent: () =>
            import('./pages/not-authenticated-page/not-authenticated.component').then(
                m => m.NotAuthenticatedComponent
            ),
    },
    {
        path: 'create-an-account',
        canActivate: [IsNotAuthenticatedGuard],
        loadComponent: () =>
            import('./pages/user-pages/user-register-page/user-register-page.component').then(
                m => m.UserRegisterPageComponent
            ),
    },
    {
        path: 'forgot-password',
        canActivate: [IsNotAuthenticatedGuard],
        loadComponent: () =>
            import('./pages/user-pages/user-password-reset-page/user-password-reset-page.component').then(
                m => m.UserPasswordResetPageComponent
            ),
    },
    {
        path: 'forgot-password/reset/:uid/:token',
        loadComponent: () =>
            import('./pages/user-pages/user-new-password-page/user-new-password-page.component').then(
                m => m.UserNewPasswordPageComponent
            ),
    },
    {
        path: 'not-found',
        loadComponent: () =>
            import('./pages/not-found-page/not-found-page.component').then(
                m => m.NotFoundPageComponent
            ),
    },

    {
        path: 'checkout',
        children: [
            {
                path: 'payment-success/:payment_id',
                canActivate: [IsAuthenticatedGuard],
                loadComponent: () =>
                    import('./pages/checkout-pages/payment-success-page/payment-success-page.component').then(
                        m => m.PaymentSuccessPageComponent
                    ),
            },
            {
                path: ':departure_id/:reservation_id/:payment_id/payment-choice',
                canActivate: [IsAuthenticatedGuard],
                loadComponent: () =>
                    import('./pages/checkout-pages/payment-choice-page/payment-choice-page.component').then(
                        m => m.PaymentChoicePageComponent
                    ),
            },
            {
                path: ':departure_id/:reservation_id/:payment_id/payment-card',
                canActivate: [IsAuthenticatedGuard],
                loadComponent: () =>
                    import('./pages/checkout-pages/payment-card-page/payment-card-page.component').then(
                        m => m.PaymentCardPageComponent
                    ),
            },
            {
                path: ':departure_id/:reservation_id/:payment_id/payment-amex',
                canActivate: [IsAuthenticatedGuard],
                loadComponent: () =>
                    import('./pages/checkout-pages/payment-amex-page/payment-amex-page.component').then(
                        m => m.PaymentAmexPageComponent
                    ),
            },
            {
                path: ':departure_id/:reservation_id/:payment_id/payment-revolut-pay',
                canActivate: [IsAuthenticatedGuard],
                loadComponent: () =>
                    import('./pages/checkout-pages/payment-revolut-pay-page/payment-revolut-pay-page.component').then(
                        m => m.PaymentRevolutPayPageComponent
                    ),
            },
            {
                path: ':departure_id/:reservation_id/:payment_id/payment-bank-transfer',
                canActivate: [IsAuthenticatedGuard],
                loadComponent: () =>
                    import('./pages/checkout-pages/payment-bank-transfer-page/payment-bank-transfer-page.component').then(
                        m => m.PaymentBankTransferPageComponent
                    ),
            },
            {
                path: ':departure_id/:reservation_id/billing',
                loadComponent: () =>
                    import('./pages/checkout-pages/billing-page/billing-page.component').then(
                        m => m.BillingPageComponent
                    ),
            },
            {
                path: ':departure_id',
                resolve: { featureFlag: FeatureFlagResolver },
                data: {
                    featureFlags: [
                        {
                            featureFlag: FeatureFlags.SELF_BOOKING,
                            value: true,
                            persist: true,
                        },
                    ],
                },
                loadComponent: () =>
                    import('./pages/checkout-pages/checkout-page/checkout-page.component').then(
                        m => m.CheckoutPageComponent
                    ),
            },
        ],
    },

    {
        path: 'office',
        canActivate: [IsAuthenticatedGuard, IsStaffGuard],
        loadComponent: () =>
            import('./pages/office-pages/office-pages.component').then(
                m => m.OfficePagesComponent
            ),
        loadChildren: () =>
            import('./pages/office-pages/office.routes').then(
                m => m.officeRoutes
            ),
    },
    {
        path: 'dashboard/impersonate/:clientId',
        // Outside the child's providers, so it checks the staff member's session.
        canActivate: [IsAuthenticatedGuard],
        children: [
            {
                path: '',
                providers: provideImpersonation(),
                canActivate: [startImpersonationGuard],
                children: [
                    verifyEmailRoute,
                    {
                        path: '',
                        canActivate: [IsVerifiedGuard],
                        children: dashboardRoutes,
                    },
                ],
            },
        ],
    },
    {
        path: 'dashboard',
        canActivate: [IsAuthenticatedGuard, IsVerifiedGuard],
        children: dashboardRoutes,
    },
    {
        path: 'invoice',
        children: [
            {
                path: ':invoicePublicIdentifier',
                loadComponent: () =>
                    import('./pages/public-invoice-page/public-invoice-page.component').then(
                        m => m.PublicInvoicePageComponent
                    ),
            },
            {
                path: ':invoicePublicIdentifier/payment/:publicIdentifier/billing-address',
                loadComponent: () =>
                    import('./pages/public-invoice-billing-address-page/public-invoice-billing-address-page.component').then(
                        m => m.PublicInvoiceBillingAddressPageComponent
                    ),
            },
            {
                path: ':invoicePublicIdentifier/payment/:publicIdentifier/payment-methods',
                loadComponent: () =>
                    import('./pages/public-invoice-payment-methods-page/public-invoice-payment-methods-page.component').then(
                        m => m.PublicInvoicePaymentMethodsPageComponent
                    ),
            },
            {
                path: ':invoicePublicIdentifier/payment/:publicIdentifier/payment-card',
                loadComponent: () =>
                    import('./pages/public-invoice-payment-card-page/public-invoice-payment-card-page.component').then(
                        m => m.PublicInvoicePaymentCardPageComponent
                    ),
            },
            {
                path: ':invoicePublicIdentifier/payment/:publicIdentifier/payment-revolut-pay',
                loadComponent: () =>
                    import('./pages/public-invoice-payment-revolut-pay-page/public-invoice-payment-revolut-pay-page.component').then(
                        m => m.PublicInvoicePaymentRevolutPayPageComponent
                    ),
            },
            {
                path: ':invoicePublicIdentifier/payment/:publicIdentifier/payment-amex',
                loadComponent: () =>
                    import('./pages/public-invoice-payment-amex-page/public-invoice-payment-amex-page.component').then(
                        m => m.PublicInvoicePaymentAmexPageComponent
                    ),
            },
            {
                path: ':invoicePublicIdentifier/payment/:publicIdentifier/payment-bank-transfer',
                loadComponent: () =>
                    import('./pages/public-invoice-payment-bank-transfer-page/public-invoice-payment-bank-transfer-page.component').then(
                        m => m.PublicInvoicePaymentBankTransferPageComponent
                    ),
            },
            {
                path: ':invoicePublicIdentifier/payment/:publicIdentifier/payment-overview',
                loadComponent: () =>
                    import('./pages/public-invoice-payment-overview-page/public-invoice-payment-overview-page.component').then(
                        m => m.PublicInvoicePaymentOverviewPageComponent
                    ),
            },
            {
                path: ':invoicePublicIdentifier/payment/:publicIdentifier/create-account',
                loadComponent: () =>
                    import('./pages/public-invoice-payment-create-account-page/public-invoice-payment-create-account-page.component').then(
                        m => m.PublicInvoicePaymentCreateAccountPageComponent
                    ),
            },
        ],
    },
    {
        path: 'invitation',
        pathMatch: 'full',
        canActivate: [IsNotAuthenticatedGuard],
        loadComponent: () =>
            import('./pages/invitation-page/invitation-page.component').then(
                m => m.InvitationPageComponent
            ),
    },
    {
        path: 'invitation/:referralCode',
        canActivate: [IsNotAuthenticatedGuard],
        loadComponent: () =>
            import('./pages/invitation-page/invitation-page.component').then(
                m => m.InvitationPageComponent
            ),
    },
    {
        path: '**',
        loadComponent: () =>
            import('./pages/not-found-page/not-found-page.component').then(
                m => m.NotFoundPageComponent
            ),
    },
];
